import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { store, RootState } from '../../redux/store';
import { okaoAxios } from '../../helpers/axios';
import { fetchRestaurantByAlias } from '../Restaurant/slice';

export interface DishImageSuggestionDTO {
  id: string;
  restaurantId: string;
  dishId: string | null;
  url: string;
  source: string;
  used: boolean;
  created?: Date;
  updated?: Date;
}

interface DishImageSuggestionsState {
  items: DishImageSuggestionDTO[];
  status: 'idle' | 'loading' | 'error';
  error?: string;
  isGenerating: boolean;
  generateError?: string;
}

const initialState: DishImageSuggestionsState = {
  items: [],
  status: 'idle',
  error: undefined,
  isGenerating: false,
  generateError: undefined
};

// Fetch dish image suggestions for a restaurant
export const fetchDishImageSuggestions = createAsyncThunk<
  DishImageSuggestionDTO[],
  { restaurantId: string }
>('dishImageSuggestions/fetch', async ({ restaurantId }) => {
  const auth = store.getState().secure.accessToken ?? '';
  const baseUrl = process.env.REACT_APP_DIS_URL ?? window.location.origin;
  const response = await okaoAxios(auth).get<DishImageSuggestionDTO[]>(
    `${baseUrl}/admin/restaurants/${restaurantId}/dish-image-suggestions`
  );
  return response.data;
});

// Generate dish image suggestions for a restaurant
export const generateDishImageSuggestions = createAsyncThunk<
  DishImageSuggestionDTO[],
  { restaurantId: string; overwriteExisting?: boolean }
>('dishImageSuggestions/generate', async ({ restaurantId, overwriteExisting = false }) => {
  const auth = store.getState().secure.accessToken ?? '';
  const baseUrl = process.env.REACT_APP_DIS_URL ?? window.location.origin;
  const response = await okaoAxios(auth).post<DishImageSuggestionDTO[]>(
    `${baseUrl}/admin/restaurants/${restaurantId}/dish-image-suggestions/generate`,
    null,
    {
      params: { overwriteExisting }
    }
  );
  return response.data;
});

// Attach a suggestion to a dish
export const attachDishImageSuggestion = createAsyncThunk<
  { restaurantId: string; dishId: string },
  { restaurantId: string; dishId: string; suggestionId: string; alias?: string }
>('dishImageSuggestions/attach', async ({ restaurantId, dishId, suggestionId, alias }, thunkAPI) => {
  const auth = store.getState().secure.accessToken ?? '';
  const baseUrl = process.env.REACT_APP_DIS_URL ?? window.location.origin;
  
  // POST to attach the suggestion
  await okaoAxios(auth).post(
    `${baseUrl}/admin/restaurants/${restaurantId}/dishes/${dishId}/attach-suggestion/${suggestionId}`
  );

  // After the backend has updated dish.imageUrl,
  // refresh the restaurant so the UI reflects the new image.
  if (alias != null && alias !== '') {
    await thunkAPI.dispatch(fetchRestaurantByAlias(alias));
  }

  // Return info that can be used in extraReducers if needed
  return { restaurantId, dishId };
});

export const dishImageSuggestionsSlice = createSlice({
  name: 'dishImageSuggestions',
  initialState,
  reducers: {
    clearSuggestions: (state) => {
      state.items = [];
      state.status = 'idle';
      state.error = undefined;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDishImageSuggestions.pending, (state) => {
        state.status = 'loading';
        state.error = undefined;
      })
      .addCase(fetchDishImageSuggestions.fulfilled, (state, action: PayloadAction<DishImageSuggestionDTO[]>) => {
        state.items = action.payload;
        state.status = 'idle';
        state.error = undefined;
      })
      .addCase(fetchDishImageSuggestions.rejected, (state, action) => {
        state.status = 'error';
        state.error = action.error.message ?? 'Failed to fetch dish image suggestions';
      })
      .addCase(attachDishImageSuggestion.pending, (state) => {
        // Optionally mark suggestion as used in local state
      })
      .addCase(attachDishImageSuggestion.fulfilled, (state, action) => {
        // Mark the suggestion as used
        const suggestion = state.items.find(s => s.id === action.meta.arg.suggestionId);
        if (suggestion != null) {
          suggestion.used = true;
        }
      })
      .addCase(attachDishImageSuggestion.rejected, (state, action) => {
        // Error handling - could show toast via restaurant slice
      })
      .addCase(generateDishImageSuggestions.pending, (state) => {
        state.isGenerating = true;
        state.generateError = undefined;
      })
      .addCase(generateDishImageSuggestions.fulfilled, (state, action: PayloadAction<DishImageSuggestionDTO[]>) => {
        state.isGenerating = false;
        // Replace items with generated suggestions
        state.items = action.payload;
        state.generateError = undefined;
      })
      .addCase(generateDishImageSuggestions.rejected, (state, action) => {
        state.isGenerating = false;
        state.generateError = action.error.message ?? 'Failed to generate dish image suggestions';
      });
  }
});

export const { clearSuggestions } = dishImageSuggestionsSlice.actions;

// Selectors
export const selectDishImageSuggestions = (state: RootState): DishImageSuggestionDTO[] =>
  state.dishImageSuggestions.items;

export const selectDishImageSuggestionsStatus = (state: RootState): 'idle' | 'loading' | 'error' =>
  state.dishImageSuggestions.status;

export const selectDishImageSuggestionsError = (state: RootState): string | undefined =>
  state.dishImageSuggestions.error;

export const selectDishImageSuggestionsIsGenerating = (state: RootState): boolean =>
  state.dishImageSuggestions.isGenerating;

export const selectDishImageSuggestionsGenerateError = (state: RootState): string | undefined =>
  state.dishImageSuggestions.generateError;

export default dishImageSuggestionsSlice.reducer;
